import { useEnvironment } from '@livekatsomo/web/hooks';
import {
  CollaborationCursorExtension,
  ListenToRemoteCursorUpdates,
  RemoveCursorPositionOnDestroy,
  SendCurrentCursorPosition,
} from '@santicon/prosemirror-firebase-collaboration-cursor-plugin';
import {
  FirestoreCollaborationExtension,
  GetInitialDocumentContentAndVersion,
  ListenToDocumentUpdates,
  SaveDocumentContent,
  SendDocumentUpdateSteps,
} from '@santicon/prosemirror-firestore-collaboration-plugin';
import { Editor } from '@tiptap/react';
import { forwardRef } from 'react';
import { TipTapEditor, TipTapEditorProps } from '../TipTapEditor/TipTapEditor';

export interface CollabEditorProps {
  versionListener?: (version: number) => void;
  onAddImage?: TipTapEditorProps['onAddImage'];
  listenToDocumentUpdates: ListenToDocumentUpdates;
  getInitialDocumentContent: GetInitialDocumentContentAndVersion;
  saveDocumentContent: SaveDocumentContent;
  sendDocumentUpdateSteps: SendDocumentUpdateSteps;
  listenToRemoteCursorUpdates: ListenToRemoteCursorUpdates;
  removeCursorPositionOnDestroy: RemoveCursorPositionOnDestroy;
  sendCurrentCursorPosition: SendCurrentCursorPosition;
}

/**
 * A collaborative editor component that uses ProseMirror, Firestore, and Firebase to enable real-time collaboration.
 * @param versionListener A function that is called whenever the version of the document changes.
 * @param onAddImage A function that is called when an image is added to the editor.
 * @param listenToDocumentUpdates A function that listens to updates to the document.
 * @param getInitialDocumentContent A function that retrieves the initial content of the document.
 * @param saveDocumentContent A function that saves the content of the document.
 * @param sendDocumentUpdateSteps A function that sends the update steps of the document.
 * @param listenToRemoteCursorUpdates A function that listens to updates to remote cursors.
 * @param removeCursorPositionOnDestroy A function that removes the cursor position when the component is destroyed.
 * @param sendCurrentCursorPosition A function that sends the current cursor position.
 */
export const CollabEditor = forwardRef<Editor, CollabEditorProps>(
  (
    {
      getInitialDocumentContent,
      listenToDocumentUpdates,
      listenToRemoteCursorUpdates,
      removeCursorPositionOnDestroy,
      saveDocumentContent,
      sendCurrentCursorPosition,
      sendDocumentUpdateSteps,
      versionListener,
      onAddImage,
    },
    ref,
  ) => {
    const { development } = useEnvironment();

    return (
      <TipTapEditor
        onAddImage={onAddImage}
        showHTML={development}
        debug={development}
        extensions={[
          FirestoreCollaborationExtension.configure({
            debounceTime: 1000,
            listenToDocumentUpdates,
            getInitialDocumentContent,
            saveDocumentContent,
            sendDocumentUpdateSteps,
            versionListener,
          }),
          CollaborationCursorExtension.configure({
            debounceTime: 1000,
            listenToRemoteCursorUpdates,
            removeCursorPositionOnDestroy,
            sendCurrentCursorPosition,
          }),
        ]}
        ref={ref}
      />
    );
  },
);

export default CollabEditor;
